import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useGlobalStore } from '@/stores';
import {
  AddCartItemBody,
  Cart,
  UpdateCartShippingTypeBody,
} from '@/api/carts/carts.types.ts';
import { cartsApi } from '@/api';

export const useCart = (
  options?: Partial<UseQueryOptions<Cart, AxiosError>>,
) => {
  const store = useGlobalStore();
  const contactEmployeeId = store.contactEmployeeId;
  if (!contactEmployeeId) {
    throw new Error('Contact employee ID is not set');
  }
  return useQuery<Cart, AxiosError>({
    queryKey: ['cart', contactEmployeeId],
    queryFn: () => cartsApi.getCart(contactEmployeeId).then((res) => res.data),
    ...options,
  });
};

export const useUpdateCartShippingType = (
  id?: number,
  options?: UseMutationOptions<void, AxiosError, UpdateCartShippingTypeBody>,
) => {
  const queryClient = useQueryClient();
  const store = useGlobalStore();
  let contactEmployeeId = store.contactEmployeeId as number;
  if (id != null) {
    contactEmployeeId = id;
  }
  return useMutation<void, AxiosError, UpdateCartShippingTypeBody>({
    mutationFn: (body) =>
      cartsApi.updateCartShippingType(contactEmployeeId, body).then((res) => {
        queryClient.invalidateQueries({
          queryKey: ['cart', contactEmployeeId],
        });
        return res.data;
      }),
    ...options,
  });
};

export const useUpsertCartItem = (
  id?: number,
  options?: UseMutationOptions<void, AxiosError, AddCartItemBody>,
) => {
  const queryClient = useQueryClient();
  const store = useGlobalStore();
  let contactEmployeeId = store.contactEmployeeId as number;
  if (id != null) {
    contactEmployeeId = id;
  }
  return useMutation<void, AxiosError, AddCartItemBody>({
    mutationFn: (body) =>
      cartsApi
        .upsertCartItem(contactEmployeeId, body)
        .then((res) => {
          return res.data;
        })
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: ['cart'],
          });
        }),
    ...options,
  });
};
